import {
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox as MuiCheckbox,
} from "@mui/material";
import React from "react";
import defaultStyles, {
  StylesClasses,
} from "src/components/feature/ProjectDetailView/styles";
import ViewForm from "../ViewForm";
import {
  dateAndTimeStamp,
  downloadS3File,
  getFormattedDate,
} from "src/utils/helper";
import Card from "../ImageCard";
import ViewImages from "../ViewImage";
import MapComponent from "src/components/common/KML";
import { PROJECT_DOCUMENT_TYPE_MAP, ProjectType } from "src/constants/common";
import { getStyles } from "src/styles/theme";

const ProjectOverview = ({
  projectData,
  siteVisitData,
  projectDataByEpid,
}: {
  projectData: any;
  siteVisitData?: any;
  projectDataByEpid?: any;
}) => {
  const styles = getStyles<StylesClasses>(defaultStyles);

  const kmlDocument = projectData?.documentDetail?.find(
    (doc) => doc.type === "LAND_KML_FILE"
  );
  const kmlFileUrl = kmlDocument?.documentUrl;

  //video
  const droneVideoFromID = projectData?.documentDetail?.find(
    (doc) => doc.type === "DRONE_VIDEO"
  );

  const droneVideoUrl = droneVideoFromID?.documentUrl;

  const adminApproveDocs = projectData?.documentDetail?.find(
    (doc) => doc.type === "ADMIN_APPROVAL_DOC"
  );
  const adminApproveUrl = adminApproveDocs?.documentUrl;

  const adminRejectDocs = projectData?.documentDetail?.find(
    (doc) => doc.type === "ADMIN_REJECT_DOC"
  );
  const adminRejectUrl = adminRejectDocs?.documentUrl;

  // Helper function to parse and validate siteVisitedByEntity
  const parseSiteVisitedByEntity = () => {
    try {
      const data = projectDataByEpid?.siteVisitedByEntity;

      // Check if data exists
      if (!data) return [];

      // If it's a string, try to parse it
      if (typeof data === "string") {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      }

      // If it's already an array, return it
      if (Array.isArray(data)) {
        return data;
      }

      // If it's an object, wrap it in an array
      if (typeof data === "object") {
        return [data];
      }

      return [];
    } catch (error) {
      console.error("Error parsing siteVisitedByEntity:", error);
      return [];
    }
  };

  const siteVisitedByEntityList = parseSiteVisitedByEntity();

  return (
    <Box style={{ width: "100%" }}>
      {projectData && projectData?.length !== 0 ? (
        <>
          <Box {...styles("projectName")}>
            Activity Registration No: {projectData?.registrationNo}
          </Box>
          <Box {...styles("projectName")}>
            Activity Registration No: {projectDataByEpid?.id}
          </Box>
          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>Implementing Officer Details</Box>

            <ViewForm
              label="Name"
              value={projectData?.implementingOfficer?.firstName}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Email"
              value={projectData?.implementingOfficer?.email}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Phone Number"
              value={`${
                projectData?.implementingOfficer?.countryCode ?? "+91"
              } ${projectData?.implementingOfficer?.phoneNumber}`}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Division"
              value={projectData?.implementingOfficer?.division}
              valueStyles={{ fontWeight: "400" }}
            />
          </Box>

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>Entity Details</Box>

            <ViewForm
              label="Name"
              value={projectDataByEpid?.user?.firstName}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Email"
              value={projectDataByEpid?.user?.email}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Phone Number"
              value={projectDataByEpid?.user?.phoneNumber}
              valueStyles={{ fontWeight: "400" }}
            />
          </Box>

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>
              Eco-Restoration Block Location Details
            </Box>
            <ViewForm
              label="State"
              value={projectData?.address?.stateName}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="District"
              value={projectData?.address?.districtName}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Circle"
              value={projectData?.landSpecification?.circleName}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Forest Division"
              value={projectData?.implementingOfficer?.division}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Revenue Block"
              value={projectData?.landSpecification?.revenueBlock}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Range"
              value={projectData?.landSpecification?.range}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Compartment Number"
              value={projectData?.landSpecification?.compartmentNumber}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Beat"
              value={projectData?.landSpecification?.beat}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Survey No"
              value={projectData?.address?.khasraNo}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="TreeCanopy Density"
              value={(
                +projectData?.landSpecification?.treeCanopyDensity || 0
              ).toFixed(2)}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Total Land Area"
              value={`${(
                +projectData?.landSpecification?.totalArea || 0
              ).toFixed(2)} ${projectData?.areaUnit}`}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Selected Area"
              value={`${(
                +projectData?.landSpecification?.occupiedArea || 0
              ).toFixed(2)} ${projectData?.areaUnit}`}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Slope"
              value={projectData?.landSpecification?.slope}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Terrain (Slope %)"
              value={projectData?.landSpecification?.terrain}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Slope and Terrain"
              value={projectData?.landSpecification?.slopeAndTerrain}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Climatic Zone"
              value={projectData?.landSpecification?.climaticZone}
              valueStyles={{ fontWeight: "400" }}
            />
            <ViewForm
              label="Accessibility with Distance"
              value={projectData?.landSpecification?.accessibility}
              valueStyles={{ fontWeight: "400" }}
            />
          </Box>

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>Eco-Restoration Block Details.</Box>

            <ViewForm
              label="Land Type"
              value={projectData?.landType}
              valueStyles={{ fontWeight: "400" }}
            />

            <Box
              {...styles("subHeading")}
              style={{ color: "#559891ff", fontWeight: 600, marginTop: "20px" }}
            >
              Edaphic Details
            </Box>
            <Box {...styles("group")}>
              <ViewForm
                label="Edaphic Details"
                value={projectData?.landSpecification?.edaphicDetails}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Soil Type"
                value={projectData?.landSpecification?.soilType}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Soil Depth (cm) & Texture"
                value={projectData?.landSpecification?.soilDepthAndTexture}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Soil Moisture Regime"
                value={projectData?.landSpecification?.soilMoistureRegime}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Soil pH and fertility status"
                value={projectData?.landSpecification?.soilpH}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>

            <Box
              {...styles("subHeading")}
              style={{ color: "#559891ff", fontWeight: 600, marginTop: "20px" }}
            >
              Hydrological Conditions
            </Box>
            <Box {...styles("group")}>
              <ViewForm
                label="Hydrological Conditions"
                value={projectData?.landSpecification?.hydrologicalConditions}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Source Of Water"
                value={projectData?.landSpecification?.sourceOfWater}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Average Ground water level (meters)"
                value={projectData?.landSpecification?.waterSupplyLines}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Distance to Nearest Water Source (km)"
                value={
                  projectData?.landSpecification?.distanceToNearestWaterSource
                }
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Average Rainfall (milli meters)"
                value={projectData?.landSpecification?.averagRainfall}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>

            <Box {...styles("group")}>
              <ViewForm
                label="Forest Type"
                value={projectData?.landSpecification?.forestType}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Type Of Vegetation"
                value={projectData?.landSpecification?.typeOfVegetation}
                valueStyles={{ fontWeight: "400" }}
              />
              <Box {...styles("group")}>
                <ViewForm
                  label="Presence of existing trees inside Land parcel"
                  tableValue={(() => {
                    const raw = projectData?.landSpecification?.seedlingDetails;
                    try {
                      const parsed =
                        typeof raw === "string" ? JSON.parse(raw) : raw;
                      if (
                        parsed &&
                        Array.isArray(parsed) &&
                        parsed.length > 0
                      ) {
                        return (
                          <table
                            width="500"
                            // border={1}
                            style={{ borderCollapse: "collapse" }}
                            align="center"
                          >
                            <thead>
                              <tr>
                                <th>Common Name</th>
                                <th>Botanical Name</th>
                                <th>Number of Trees</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parsed.map((item, index) => (
                                <tr
                                  key={`${item.commonName}-${item.botanicalName}-${index}`}
                                >
                                  <td style={{ textAlign: "center" }}>
                                    {item.commonName}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.botanicalName}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.numberOfSeedlings}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      }
                      return "-";
                    } catch {
                      return "-";
                    }
                  })()}
                />

                {projectData?.treeSpecificationDetail?.map((data) => {
                  return (
                    <Box
                      {...styles("group")}
                      key={data?.id ?? data?.speciesName}
                    >
                      {data?.treeType === 0 ? (
                        <>
                          <ViewForm
                            label="Existing Tree Species"
                            value={data?.speciesName}
                          />
                          <ViewForm
                            label="Existing Tree count"
                            value={data?.treeCount}
                          />
                        </>
                      ) : (
                        <>
                          <ViewForm
                            label="Tree Species Proposed"
                            value={data?.speciesName}
                          />
                          {!!data?.treeSpecies && (
                            <>
                              <ViewForm
                                label="Name"
                                value={data?.treeSpecies?.name}
                              />
                              <ViewForm
                                label="Species Name"
                                value={data?.treeSpecies?.speciesName}
                              />
                              <ViewForm
                                label="Species Type"
                                value={data?.treeSpecies?.speciesType}
                              />
                            </>
                          )}
                          <ViewForm
                            label="Number Of Trees Proposed"
                            value={data?.treeCount}
                          />
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <ViewForm
                label="Invasive Species Details"
                value={
                  projectData?.landSpecification?.invasiveSpeciesDetails
                    ? "Yes"
                    : "No"
                }
                valueStyles={{ fontWeight: "400" }}
              />
              {projectData?.landSpecification?.invasiveSpeciesDetails && (
                <ViewForm
                  label="Invasive Species Details"
                  value={projectData?.landSpecification?.invasiveSpeciesDetails}
                  valueStyles={{ fontWeight: "400" }}
                />
              )}
              <ViewForm
                label="Existing Fauna"
                value={projectData?.landSpecification?.existingFauna}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>

            <Box
              {...styles("subHeading")}
              style={{ color: "#559891ff", fontWeight: 600, marginTop: "20px" }}
            >
              Socio-Environmental Context
            </Box>
            <Box {...styles("group")}>
              <ViewForm
                label="Name of the nearest village or habitation"
                value={projectData?.landSpecification?.soilCharacteristics}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Distance to nearest habitation (km)"
                value={
                  projectData?.landSpecification?.proximityToLocalCommunities
                }
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Rights And Usage"
                value={projectData?.landSpecification?.rightsAndUsage}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Community Dependency"
                value={projectData?.landSpecification?.communityDependency}
                valueStyles={{ fontWeight: "400" }}
              />
              <ViewForm
                label="Any Existing Restrictions"
                value={projectData?.landSpecification?.anyExistingRestrictions}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>

            {projectData?.projectType === ProjectType.TREE_PLANTATION && (
              <>
                <Box {...styles("subHeading")}>Tree Specifications:</Box>
                <ViewForm
                  label="Start Date of Plantation (DD/MM/YYYY)"
                  value={getFormattedDate(projectData?.projectStartDate)}
                  valueStyles={{ fontWeight: "400" }}
                />
                <ViewForm
                  label="Activity Duration (in years)"
                  value={projectData?.projectDuration}
                  valueStyles={{ fontWeight: "400" }}
                />
              </>
            )}
          </Box>
          {projectData?.landSpecification?.description && (
            <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
              <Box {...styles("heading")}>Additional Information</Box>
              <ViewForm
                label="Additional Information"
                value={projectData?.landSpecification?.description}
                valueStyles={{ fontWeight: "400" }}
              />
            </Box>
          )}

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>KML </Box>

            {kmlFileUrl && (
              <>
                <ViewImages
                  fileUrl={kmlFileUrl}
                  label={"KML File"}
                  onDownload={() => {
                    const regNo = projectData?.registrationNo;
                    const filename = regNo
                      ? `kml_${regNo}.kml`
                      : "kml_file.kml";
                    downloadS3File(kmlFileUrl, filename);
                  }}
                />
              </>
            )}

            <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
              {kmlFileUrl && <MapComponent kmlUrl={kmlFileUrl} />}
            </Box>
          </Box>

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>Geotag Images </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                gap: 2,
                mt: 2,
              }}
            >
              {projectData?.images?.map((item, idx) => {
                return (
                  <Card
                    key={item.id}
                    title={`GeoTag Image`}
                    subheader={dateAndTimeStamp(item?.updated_at, "IST")}
                    image={item.imageUrl}
                  />
                );
              })}
            </Box>
          </Box>

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>Drone Video </Box>

            {droneVideoUrl && (
              <Box>
                <video
                  src={droneVideoUrl}
                  controls
                  width="100%"
                  height="400px"
                  style={{ maxHeight: "400px", borderRadius: "8px" }}
                />
              </Box>
            )}
          </Box>

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>While Selection Details</Box>

            <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
              <Box {...styles("heading")}>Utilization</Box>
              {!!projectDataByEpid?.utilization && (
                <ViewForm
                  label="Primary"
                  value={projectDataByEpid?.utilization?.primary?.label}
                  valueStyles={{ fontWeight: "400" }}
                />
              )}
              {!!projectDataByEpid?.utilization?.secondary && (
                <ViewForm
                  label="Secondary"
                  value={projectDataByEpid?.utilization?.secondary?.label}
                  valueStyles={{ fontWeight: "400", marginTop: "10px" }}
                />
              )}
            </Box>

            {projectDataByEpid?.nonTradableCredit && (
              <>
                <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                  <Box {...styles("heading")}>Entity Declaration</Box>
                  <Box>
                    The Green Credits issued for tree plantation under GCP shall
                    be non-tradable.
                  </Box>
                  <Box mt={2}>
                    The Green credits issued for the tree plantation under the
                    GCP shall be non-transferable except between the holding
                    company and its subsidiary companies as mentioned by the GCA
                    on the GCP portal at the time of registration of the GCA.
                  </Box>
                </Box>
              </>
            )}

            {projectDataByEpid?.siteVisitConfirmation && (
              <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                <Box {...styles("heading")}>Entity Undertaking</Box>
                <FormControlLabel
                  control={<MuiCheckbox checked={true} disabled />}
                  label="We hereby confirm our participation for restoration of the above-mentioned land parcel."
                />
              </Box>
            )}
          </Box>

          <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
            <Box {...styles("heading")}>Site Visit Details</Box>
            {(projectDataByEpid?.siteVisitConfirmation ||
              projectDataByEpid?.siteDeselect) &&
              siteVisitedByEntityList.length > 0 && (
                <>
                  <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                    <Box {...styles("heading")}>Name of Representative</Box>
                    <table
                      width="100%"
                      style={{ borderCollapse: "collapse", marginTop: "10px" }}
                    >
                      <thead>
                        <tr style={{ borderBottom: "2px solid #ddd" }}>
                          <th
                            style={{
                              padding: "8px",
                              textAlign: "left",
                              fontWeight: "600",
                            }}
                          >
                            Name
                          </th>
                          <th
                            style={{
                              padding: "8px",
                              textAlign: "left",
                              fontWeight: "600",
                            }}
                          >
                            Designation
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {siteVisitedByEntityList.map(
                          (representative, index) => (
                            <tr
                              key={index}
                              style={{ borderBottom: "1px solid #eee" }}
                            >
                              <td style={{ padding: "8px", textAlign: "left" }}>
                                {representative?.name?.trim() || "-"}
                              </td>
                              <td style={{ padding: "8px", textAlign: "left" }}>
                                {representative?.designation?.trim() || "-"}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </Box>
                </>
              )}
            {projectDataByEpid?.siteVisitConfirmation && (
              <>
                <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                  <Box {...styles("heading")}>Site Selection Remark</Box>
                  <ViewForm
                    label="Remarks"
                    value={projectDataByEpid?.siteVisitConfirmationRemarks}
                    valueStyles={{ fontWeight: "400" }}
                  />
                  <ViewForm
                    label="Date"
                    value={projectDataByEpid?.siteVisitFirstDate}
                    valueStyles={{ fontWeight: "400" }}
                  />
                </Box>
              </>
            )}
            {projectDataByEpid?.siteDeselect && (
              <>
                <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                  <Box {...styles("heading")}>Site Deselect Remark</Box>
                  <ViewForm
                    label="Remarks"
                    value={projectDataByEpid?.siteDeselectReason}
                    valueStyles={{ fontWeight: "400" }}
                  />
                  <ViewForm
                    label="Date"
                    value={projectDataByEpid?.siteVisitFirstDate}
                    valueStyles={{ fontWeight: "400" }}
                  />
                </Box>
              </>
            )}

            {projectDataByEpid?.siteVisitDocs &&
              projectDataByEpid?.siteVisitDocs.length > 0 && (
                <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                  <Box {...styles("heading")}>Site Visit Documents</Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(250px, 1fr))",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {projectDataByEpid?.siteVisitDocs?.map((doc, idx) => (
                      <Card
                        key={doc?.fileId || idx}
                        title={`Site Visit Document ${idx + 1}`}
                        subheader={dateAndTimeStamp(doc?.uploadedAt, "IST")}
                        image={doc?.documentUrl}
                      />
                    ))}
                  </Box>
                </Box>
              )}
          </Box>

          {projectDataByEpid?.status === "ACKNOWLEDGE_BY_APPROVER" && (
            <>
              <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                <Box {...styles("heading")}>Admin Approve Remark</Box>
                <ViewForm
                  label="Admin Approve Remark"
                  value={projectDataByEpid?.adminSelectionRemark}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>
              <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                <Box {...styles("heading")}>Admin Approve Document</Box>
                <Box {...styles("documentGroup")}>
                  {projectDataByEpid?.adminApproveDocs?.map((doc, idx) => (
                    <ViewImages
                      key={doc?.fileId || idx}
                      fileUrl={doc?.documentUrl}
                      label={""}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {projectDataByEpid?.status === "DESELECT" && (
            <>
              <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                <Box {...styles("heading")}>Admin Reject Remark</Box>
                <ViewForm
                  label="Admin Reject Remark"
                  value={projectDataByEpid?.adminRejectRemark}
                  valueStyles={{ fontWeight: "400" }}
                />
              </Box>
              <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
                <Box {...styles("heading")}>Admin Reject Document</Box>
                <Box {...styles("documentGroup")}>
                  {projectDataByEpid?.adminRejectDocs?.map((doc, idx) => (
                    <ViewImages
                      key={doc?.fileId || idx}
                      fileUrl={doc?.documentUrl}
                      label={""}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </>
      ) : (
        <Box {...styles("loading")}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ProjectOverview;
